/**
 * Data Access Layer for Accounts (Multi-tenancy)
 * 
 * Accounts are the core tenant entity for multi-tenancy support.
 * Each account represents a separate tenant with isolated data.
 */

import { getClient, getAdminClient, hasAdminClient } from './client.js';
import { TABLES } from './tables.js';
import type {
  Account,
  AccountCreate,
  AccountUpdate,
  AccountMember,
  AccountMemberCreate,
  AccountMemberUpdate,
} from './types.js';

// ============================================
// Account Operations
// ============================================

export async function getAccount(id: string): Promise<Account | null> {
  const { data, error } = await getClient()
    .from(TABLES.accounts)
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw error;
  }

  return data as Account;
}

export async function getAccountBySlug(slug: string): Promise<Account | null> {
  const { data, error } = await getClient()
    .from(TABLES.accounts)
    .select('*')
    .eq('slug', slug)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw error;
  }

  return data as Account;
}

export async function listAccounts(filters?: {
  type?: string;
  is_active?: boolean;
}): Promise<Account[]> {
  let query = getClient().from(TABLES.accounts).select('*');

  if (filters?.type) {
    query = query.eq('type', filters.type);
  }
  if (filters?.is_active !== undefined) {
    query = query.eq('is_active', filters.is_active);
  }

  const { data, error } = await query.order('name');

  if (error) throw error;
  return (data || []) as Account[];
}

export async function createAccount(input: AccountCreate): Promise<Account> {
  const { data, error } = await getClient()
    .from(TABLES.accounts)
    .insert({
      ...input,
      type: input.type || 'tenant',
      settings: input.settings || {},
      is_active: input.is_active ?? true,
    })
    .select()
    .single();

  if (error) throw error;
  return data as Account;
}

export async function updateAccount(id: string, input: AccountUpdate): Promise<Account> {
  const { data, error } = await getClient()
    .from(TABLES.accounts)
    .update(input)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Account;
}

export async function deleteAccount(id: string): Promise<void> {
  const { error } = await getClient()
    .from(TABLES.accounts)
    .delete()
    .eq('id', id);

  if (error) throw error;
}

// ============================================
// Account Member Operations
// ============================================

export async function getAccountMember(id: string): Promise<AccountMember | null> {
  const { data, error } = await getClient()
    .from(TABLES.account_members)
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }

  return data as AccountMember;
}

export async function getAccountMemberByUserAndAccount(
  userId: string,
  accountId: string
): Promise<AccountMember | null> {
  const { data, error } = await getClient()
    .from(TABLES.account_members)
    .select('*')
    .eq('user_id', userId)
    .eq('account_id', accountId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }

  return data as AccountMember;
}

export async function listAccountMembers(accountId: string, filters?: {
  role?: string;
  is_active?: boolean;
}): Promise<AccountMember[]> {
  let query = getClient()
    .from(TABLES.account_members)
    .select('*')
    .eq('account_id', accountId);

  if (filters?.role) {
    query = query.eq('role', filters.role);
  }
  if (filters?.is_active !== undefined) {
    query = query.eq('is_active', filters.is_active);
  }

  const { data, error } = await query.order('created_at');

  if (error) throw error;
  return (data || []) as AccountMember[];
}

export async function listUserAccounts(userId: string, filters?: {
  is_active?: boolean;
}): Promise<AccountMember[]> {
  let query = getClient()
    .from(TABLES.account_members)
    .select('*')
    .eq('user_id', userId);

  if (filters?.is_active !== undefined) {
    query = query.eq('is_active', filters.is_active);
  }

  const { data, error } = await query.order('created_at');

  if (error) throw error;
  return (data || []) as AccountMember[];
}

export async function addAccountMember(input: AccountMemberCreate): Promise<AccountMember> {
  const { data, error } = await getClient()
    .from(TABLES.account_members)
    .insert({
      ...input,
      role: input.role || 'member',
      is_active: input.is_active ?? true,
    })
    .select()
    .single();

  if (error) throw error;
  return data as AccountMember;
}

export async function updateAccountMember(id: string, input: AccountMemberUpdate): Promise<AccountMember> {
  const { data, error } = await getClient()
    .from(TABLES.account_members)
    .update(input)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as AccountMember;
}

export async function removeAccountMember(id: string): Promise<void> {
  const { error } = await getClient()
    .from(TABLES.account_members)
    .delete()
    .eq('id', id);

  if (error) throw error;
}

// ============================================
// Admin Operations (bypass RLS)
// These require service role key
// ============================================

function requireAdminClient() {
  if (!hasAdminClient()) {
    throw new Error(
      'Admin client not available. Provide SUPABASE_SERVICE_ROLE_KEY to enable admin operations.'
    );
  }
  return getAdminClient()!;
}

export async function adminGetAccount(id: string): Promise<Account | null> {
  const client = requireAdminClient();
  const { data, error } = await client
    .from(TABLES.accounts)
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  return data as Account;
}

export async function adminListAccounts(filters?: {
  type?: string;
  is_active?: boolean;
}): Promise<Account[]> {
  const client = requireAdminClient();
  let query = client.from(TABLES.accounts).select('*');

  if (filters?.type) {
    query = query.eq('type', filters.type);
  }
  if (filters?.is_active !== undefined) {
    query = query.eq('is_active', filters.is_active);
  }

  const { data, error } = await query.order('name');
  if (error) throw error;
  return (data || []) as Account[];
}

export async function adminCreateAccount(input: AccountCreate): Promise<Account> {
  const client = requireAdminClient();
  const { data, error } = await client
    .from(TABLES.accounts)
    .insert({
      ...input,
      type: input.type || 'tenant',
      settings: input.settings || {},
      is_active: input.is_active ?? true,
    })
    .select()
    .single();

  if (error) throw error;
  return data as Account;
}

export async function adminUpdateAccount(id: string, input: AccountUpdate): Promise<Account> {
  const client = requireAdminClient();
  const { data, error } = await client
    .from(TABLES.accounts)
    .update(input)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Account;
}

export async function adminDeleteAccount(id: string): Promise<void> {
  const client = requireAdminClient();
  const { error } = await client
    .from(TABLES.accounts)
    .delete()
    .eq('id', id);

  if (error) throw error;
}

export async function adminAddAccountMember(input: AccountMemberCreate): Promise<AccountMember> {
  const client = requireAdminClient();
  const { data, error } = await client
    .from(TABLES.account_members)
    .insert({
      ...input,
      role: input.role || 'member',
      is_active: input.is_active ?? true,
    })
    .select()
    .single();

  if (error) throw error;
  return data as AccountMember;
}

export async function adminRemoveAccountMember(id: string): Promise<void> {
  const client = requireAdminClient();
  const { error } = await client
    .from(TABLES.account_members)
    .delete()
    .eq('id', id);

  if (error) throw error;
}

// ============================================
// Token Validation
// ============================================

/**
 * Validate an account token and return the account if valid
 * Uses the validate_account_token RPC function which bypasses RLS
 * 
 * Returns a partial Account object with: id, slug, name, type, is_active
 * For full account details, use getAccount() after validation
 */
export async function validateAccountToken(token: string): Promise<Pick<Account, 'id' | 'slug' | 'name' | 'type' | 'is_active'> | null> {
  if (!token) return null;

  try {
    // Use the RPC function that bypasses RLS
    const { data, error } = await getClient()
      .rpc('validate_account_token', { input_token: token });

    if (error) {
      console.error('Token validation error:', error);
      return null;
    }

    if (!data || data.length === 0) {
      return null;
    }

    // The RPC returns: id, slug, name, type, is_active
    return data[0] as Pick<Account, 'id' | 'slug' | 'name' | 'type' | 'is_active'>;
  } catch (error) {
    console.error('Token validation exception:', error);
    return null;
  }
}

// ============================================
// Utility Functions
// ============================================

/**
 * Get the storage bucket prefix for an account
 * Uses storage_bucket_prefix if set, otherwise falls back to slug
 */
export function getAccountStoragePrefix(account: Account): string {
  return account.storage_bucket_prefix || account.slug;
}

/**
 * Generate a storage path with account prefix for proper tenant isolation
 */
export function getAccountStoragePath(
  account: Account,
  category: string,
  fileName: string
): string {
  const prefix = getAccountStoragePrefix(account);
  return `${prefix}/${category}/${fileName}`;
}
